// features/workstations/dashboard-sections/process-checklist.js
// ═══════════════════════════════════════════════════════════
// Process Checklist (Day Trading)
// The morning ritual — 7 hard rules as a live checklist.
// ═══════════════════════════════════════════════════════════
// Treats config.criteria.hard_rules as the source of truth.
// User can tick through them before the window opens. State
// is local-only for now (resets daily); Phase 3b persists to
// workstation_events so we can track streaks.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import ShiftRead from './shift-read';

function storageKey(workstationId) {
  const today = new Date().toISOString().slice(0, 10);
  return `ws-checklist:${workstationId || 'unknown'}:${today}`;
}

export default function ProcessChecklistSection({ workstation }) {
  const rules = workstation?.config?.criteria?.hard_rules || [];
  const [checked, setChecked] = useState({});

  // Load today's state from localStorage (survives tab switches)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(storageKey(workstation?.id));
      if (raw) setChecked(JSON.parse(raw));
    } catch {
      /* noop */
    }
  }, [workstation?.id]);

  // Persist on change
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        storageKey(workstation?.id),
        JSON.stringify(checked),
      );
    } catch {
      /* noop */
    }
  }, [checked, workstation?.id]);

  if (rules.length === 0) return null;

  const completedCount = rules.filter((r) => checked[r.id]).length;
  const allDone = completedCount === rules.length;

  function toggle(id) {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <section className="ws-section ws-section-checklist">
      <div className="ws-section-header">
        <div className="ws-section-label">Process Checklist</div>
        <div className="ws-section-tag">
          {completedCount} of {rules.length}
        </div>
      </div>
      <div className="ws-section-body">
        <ul className="ws-checklist">
          {rules.map((r) => {
            const isChecked = !!checked[r.id];
            return (
              <li key={r.id} className={`ws-check-item${isChecked ? ' is-checked' : ''}`}>
                <button
                  type="button"
                  className="ws-check-btn"
                  onClick={() => toggle(r.id)}
                  aria-label={isChecked ? 'Uncheck rule' : 'Check rule'}
                >
                  <span className={`ws-check-box${isChecked ? ' is-checked' : ''}`}>
                    {isChecked && <span className="ws-check-mark">✓</span>}
                  </span>
                  <span className="ws-check-text">{r.text}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <ShiftRead>
          {allDone
            ? 'Every rule acknowledged. You are cleared to enter the window.'
            : `${rules.length - completedCount} rule${rules.length - completedCount === 1 ? '' : 's'} unchecked. Walk through each before the bell.`}
        </ShiftRead>
      </div>
    </section>
  );
}
